import { resolveTemplate } from '@mythictable/sheets';
import { toLibrarySlug } from '@/core/utils/libraryName';
import { embedCharacter } from './tokenCharacter';

type CharacterLike = Record<string, unknown>;

/**
 * The token record for `character`, minus what the caller owns (`_userid`,
 * `_campaign`, `pos`). A token carries either a link to a character or a complete
 * embedded copy of one — never a scattering of the character's fields.
 */
export function buildSpawnToken(character: CharacterLike): CharacterLike {
    const token: CharacterLike = {
        _name: canonicalName(character),
        private: character.private ?? false,
    };

    const characterId = character._id as string | undefined;
    if (characterId) token.characterId = characterId;

    if (spawnBehavior(character) === 'reference') {
        token._tokenBehavior = 'reference';
        return token;
    }

    token._tokenBehavior = 'copy';
    token.character = embedCharacter(character);
    return token;
}

/** The character's own choice, else its template's, else a link. */
function spawnBehavior(character: CharacterLike): string {
    const chosen = character.behavior;
    if (chosen === 'copy' || chosen === 'reference') return chosen;
    const template = resolveTemplate(character) as { meta?: { defaultBehavior?: string } } | undefined;
    return template?.meta?.defaultBehavior === 'copy' ? 'copy' : 'reference';
}

/** Pre-migration characters carry only `name`; every token needs `_name`. */
function canonicalName(character: CharacterLike): string | undefined {
    if (character._name) return character._name as string;
    if (character.name) return toLibrarySlug(character.name as string, '.chr');
    return undefined;
}
